// Configurable Jira status categories
// Maps status strings to semantic categories used for colors, sorting, and indicator logic.
// Persisted in IndexedDB via the general settings store.
// Auto-syncs with Jira's available statuses when connected.

import { getSetting, setSetting } from './storage';

// ==================== Types ====================

export type StatusCategory =
  | 'done'
  | 'in_progress'
  | 'release_candidate'
  | 'testing'
  | 'rework'
  | 'blocked'
  | 'cancelled'
  | 'hotfix'
  | 'todo';

export interface JiraStatusConfig {
  /** Status strings for each category (lowercase, matched via includes) */
  categories: Record<StatusCategory, string[]>;
  /** Last synced from Jira API (ISO timestamp, null if never synced) */
  lastSynced: string | null;
  /** All known status names from Jira (raw, for reference) */
  jiraStatuses: string[];
}

// ==================== Defaults ====================

export const DEFAULT_STATUS_CONFIG: JiraStatusConfig = {
  categories: {
    done: ['completed', 'done', 'live in production', 'archived', 'closed', 'production hotfix'],
    in_progress: ['in progress', 'dev testing'],
    release_candidate: ['release candidate', 'ready for release', 'deploying', 'waiting for deploy', 'deploying to prod', 'waiting for deploy to dev'],
    testing: ['qa testing', 'release testing', 'pre-production', 'pre-production code review'],
    rework: ['dev rework', 'release rework'],
    blocked: ['blocked', 'pending db', 'deployment failed', 'blocked - awaiting input', 'pending db update', 'deployment failed - dev', 'deployment failed - prod'],
    cancelled: ['canceled', 'cancelled'],
    hotfix: ['hotfix'],
    todo: ['to do', 'to discuss', 'planning'],
  },
  lastSynced: null,
  jiraStatuses: [],
};

// ==================== Storage ====================

const SETTINGS_KEY = 'jiraStatusConfig';

let cachedConfig: JiraStatusConfig | null = null;

export async function getStatusConfig(): Promise<JiraStatusConfig> {
  if (cachedConfig) return cachedConfig;
  const stored = await getSetting<JiraStatusConfig>(SETTINGS_KEY);
  cachedConfig = stored || { ...DEFAULT_STATUS_CONFIG };
  return cachedConfig;
}

export async function saveStatusConfig(config: JiraStatusConfig): Promise<void> {
  cachedConfig = config;
  await setSetting(SETTINGS_KEY, config);
}

export async function resetStatusConfig(): Promise<void> {
  cachedConfig = { ...DEFAULT_STATUS_CONFIG, categories: { ...DEFAULT_STATUS_CONFIG.categories } };
  await setSetting(SETTINGS_KEY, cachedConfig);
}

/** Invalidate the in-memory cache (e.g. after external changes) */
export function invalidateStatusCache(): void {
  cachedConfig = null;
}

// ==================== Sync-compatible getter (for components) ====================

/** Synchronous getter — returns cached config or defaults. Call getStatusConfig() first to hydrate. */
export function getStatusConfigSync(): JiraStatusConfig {
  return cachedConfig || DEFAULT_STATUS_CONFIG;
}

// ==================== Helper functions ====================

/** Check if a status string matches any string in a category */
function matchesCategory(status: string, categoryStrings: string[]): boolean {
  const s = status.toLowerCase().trim();
  return categoryStrings.some(cs => s.includes(cs));
}

/** Get the semantic category for a Jira status string */
export function getStatusCategory(status: string, config?: JiraStatusConfig): StatusCategory | null {
  const cfg = config || getStatusConfigSync();
  const cats = cfg.categories;

  // Check in priority order (more specific first)
  if (matchesCategory(status, cats.done)) return 'done';
  if (matchesCategory(status, cats.rework)) return 'rework';
  if (matchesCategory(status, cats.blocked)) return 'blocked';
  if (matchesCategory(status, cats.hotfix)) return 'hotfix';
  if (matchesCategory(status, cats.cancelled)) return 'cancelled';
  if (matchesCategory(status, cats.release_candidate)) return 'release_candidate';
  if (matchesCategory(status, cats.testing)) return 'testing';
  if (matchesCategory(status, cats.in_progress)) return 'in_progress';
  if (matchesCategory(status, cats.todo)) return 'todo';
  return null;
}

// ==================== Color mapping ====================

const CATEGORY_COLORS: Record<StatusCategory, { light: string; dark: string; unified: string }> = {
  done:              { light: 'bg-emerald-500/20 text-emerald-700 border-emerald-500/30', dark: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', unified: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  in_progress:       { light: 'bg-blue-500/20 text-blue-700 border-blue-500/30',         dark: 'bg-blue-500/20 text-blue-300 border-blue-500/30',         unified: 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  release_candidate: { light: 'bg-violet-500/20 text-violet-700 border-violet-500/30',   dark: 'bg-violet-500/20 text-violet-300 border-violet-500/30',   unified: 'bg-violet-500/20 text-violet-700 dark:text-violet-300 border-violet-500/30' },
  testing:           { light: 'bg-amber-500/20 text-amber-700 border-amber-500/30',      dark: 'bg-amber-500/20 text-amber-300 border-amber-500/30',      unified: 'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  rework:            { light: 'bg-red-500/20 text-red-700 border-red-500/30',            dark: 'bg-red-500/20 text-red-300 border-red-500/30',            unified: 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30' },
  blocked:           { light: 'bg-red-500/20 text-red-700 border-red-500/30',            dark: 'bg-red-500/20 text-red-300 border-red-500/30',            unified: 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30' },
  cancelled:         { light: 'bg-slate-500/20 text-slate-700 border-slate-500/30',      dark: 'bg-slate-500/20 text-slate-300 border-slate-500/30',      unified: 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/30' },
  hotfix:            { light: 'bg-orange-500/20 text-orange-700 border-orange-500/30',    dark: 'bg-orange-500/20 text-orange-300 border-orange-500/30',    unified: 'bg-orange-500/20 text-orange-700 dark:text-orange-300 border-orange-500/30' },
  todo:              { light: 'bg-slate-500/20 text-slate-700 border-slate-500/30',      dark: 'bg-slate-500/20 text-slate-300 border-slate-500/30',      unified: 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/30' },
};

const DEFAULT_COLOR = { light: 'bg-slate-500/20 text-slate-700 border-slate-500/30', dark: 'bg-slate-500/20 text-slate-300 border-slate-500/30', unified: 'bg-slate-500/20 text-slate-700 dark:text-slate-300 border-slate-500/30' };

/** Get Tailwind color classes for a Jira status — unified (dark: variants) */
export function getStatusColor(status: string, config?: JiraStatusConfig): string {
  const cat = getStatusCategory(status, config);
  return cat ? CATEGORY_COLORS[cat].unified : DEFAULT_COLOR.unified;
}

/** Get Tailwind color classes — dark-only mode (no dark: prefix, for components that are always dark) */
export function getStatusColorDark(status: string, config?: JiraStatusConfig): string {
  const cat = getStatusCategory(status, config);
  return cat ? CATEGORY_COLORS[cat].dark : DEFAULT_COLOR.dark;
}

// ==================== Sort order ====================

const CATEGORY_SORT_ORDER: Record<StatusCategory, number> = {
  in_progress: 0,
  release_candidate: 1,
  testing: 2,
  blocked: 3,
  rework: 4,
  hotfix: 5,
  done: 6,
  cancelled: 7,
  todo: 8,
};

/** Get numeric sort order for a Jira status (lower = higher priority) */
export function getStatusSortOrder(status: string, config?: JiraStatusConfig): number {
  const cat = getStatusCategory(status, config);
  return cat != null ? CATEGORY_SORT_ORDER[cat] : 9;
}

// ==================== Indicator helpers ====================

/** Check if a status is a "done" status */
export function isDoneStatus(status: string, config?: JiraStatusConfig): boolean {
  return getStatusCategory(status, config) === 'done';
}

/** Check if a status is a "forward" status (testing, release_candidate, or done — used for rework detection) */
export function isForwardStatus(status: string, config?: JiraStatusConfig): boolean {
  const cat = getStatusCategory(status, config);
  return cat === 'testing' || cat === 'release_candidate' || cat === 'done';
}

/** Check if a status is a "rework" status (rework or in_progress — used for rework detection) */
export function isReworkStatus(status: string, config?: JiraStatusConfig): boolean {
  const cat = getStatusCategory(status, config);
  return cat === 'rework' || cat === 'in_progress';
}

/** Get all status strings for a category */
export function getStatusStrings(category: StatusCategory, config?: JiraStatusConfig): string[] {
  const cfg = config || getStatusConfigSync();
  return cfg.categories[category];
}

/** Check if a status matches a specific category */
export function statusMatchesCategory(status: string, category: StatusCategory, config?: JiraStatusConfig): boolean {
  return getStatusCategory(status, config) === category;
}
